'use client';

import { type ReactNode } from 'react';
import { BackButton } from './back-button';
import { HeaderActions } from './header-actions';
import { useMobileNavigation } from './use-mobile-navigation';
import type { MobileHeaderVariant } from '@components/layout/page-chrome-context';

interface NavigationBarProps {
  organization_id: string;
  trailing?: ReactNode;
  variant?: MobileHeaderVariant;
}

/** Stable-height toolbar. Root screens show their title left-aligned at
 * large-title size; detail screens center a compact title beside Back. */
export function NavigationBar({
  organization_id,
  trailing,
  variant
}: NavigationBarProps) {
  const navigation = useMobileNavigation(organization_id);
  const { title, isRoot, parentHref, parentLabel, chrome } = navigation;
  const resolvedVariant = variant ?? navigation.variant;
  const trailingContent = chrome?.trailingAction ?? trailing;
  const balanceEmptyTrailing =
    Boolean(chrome?.hideDefaultActions) && !trailingContent;
  const showBack = !isRoot || Boolean(chrome?.backHref);
  const largeTitle = resolvedVariant === 'large-title';

  return (
    <div
      data-mobile-toolbar=""
      data-large-title={largeTitle}
      className="mobile-toolbar shrink-0 bg-background pt-[calc(var(--safe-top)+12px)] desktop-chrome:hidden"
    >
      <div className="grid min-h-[var(--mobile-navbar-height)] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-5">
        <div className="flex min-w-0 items-center">
          {showBack ? (
            <BackButton href={parentHref} label={parentLabel} />
          ) : null}
        </div>
        {/* PageHeader owns the accessible h1; this is the visual title.
            Plain template: cn() would merge text-<size> tokens with colors. */}
        <div
          data-mobile-title-text={largeTitle ? '' : undefined}
          aria-hidden={largeTitle || undefined}
          className={`mobile-toolbar-title min-w-0 truncate ${
            largeTitle
              ? 'text-left text-app-large-title'
              : 'text-center text-card-title'
          }`}
        >
          <span>{title}</span>
          {chrome?.status ? (
            <span className="ml-2">{chrome.status}</span>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-1.5">
          {trailingContent}
          {!chrome?.hideDefaultActions && (
            <HeaderActions organization_id={organization_id} />
          )}
          {balanceEmptyTrailing ? (
            <span className="size-11 shrink-0" aria-hidden />
          ) : null}
        </div>
      </div>
      {resolvedVariant !== 'transparent' ? (
        <div className="mobile-toolbar-divider" aria-hidden />
      ) : null}
    </div>
  );
}
